/*
  Garage Golf master template: feature videos, tabbed videos and image slider.
  Loaded by sections/gg-master-features.liquid only when that section draws entries.
  Every video[data-gg-video] plays while at least 25 percent is on screen and pauses otherwise.
  Tabs (div[data-gg-tabs]) switch by click and by Left, Right, Home and End keys; the shown
  panel's video restarts and plays, the others pause.
  Image slider (div[data-gg-slider]) adds previous and next buttons, swipe, and a 6 second auto
  advance that pauses on hover, keyboard focus and for 10 seconds after any interaction.
  With prefers-reduced-motion: reduce, no video plays on its own and nothing auto advances.
  Story cards (div[data-gg-story]) add previous and next buttons and dots to a scroll snap row
  (arrows disabled at the ends, controls hidden when every card fits) and open each card's story
  template in the section's one dialog. The dialog steps between stories, closes on Escape, locks
  page scroll while open, and returns focus to the Show more button of the story last shown.
  With a mouse only, edge zones scroll the row one card at a time and the plus opens a hover preview.
*/
(function () {
  if (window.ggMasterMedia) return;
  window.ggMasterMedia = true;

  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reduce = !!(mqReduce && mqReduce.matches);

  function listen(mq, fn) {
    if (!mq) return;
    if (mq.addEventListener) mq.addEventListener('change', fn); else if (mq.addListener) mq.addListener(fn);
  }

  function videos(root) {
    return Array.prototype.slice.call((root || document).querySelectorAll('video[data-gg-video]'));
  }

  function blocked(v) {
    var panel = v.closest('[role="tabpanel"]');
    if (panel && panel.hidden) return true;
    return false;
  }

  function tryPlay(v) {
    if (reduce || blocked(v)) return;
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }

  function stopAll() {
    videos().forEach(function (v) {
      v.removeAttribute('autoplay');
      v.autoplay = false;
      v.pause();
    });
  }

  function initVideos() {
    var list = videos();
    list.forEach(function (v) { v.muted = true; });
    if (reduce) { stopAll(); return; }
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var v = en.target;
        v.ggVisible = en.isIntersecting && en.intersectionRatio >= 0.25;
        if (v.ggVisible) tryPlay(v); else v.pause();
      });
    }, { threshold: [0, 0.25] });
    list.forEach(function (v) { io.observe(v); });
  }

  /* ---------- Tabs ---------- */
  function initTabs(box) {
    var tabs = Array.prototype.slice.call(box.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return null;
    var api = { tabs: tabs, index: 0, onUser: null };

    function select(i, focus) {
      api.index = i;
      tabs.forEach(function (t, n) {
        var on = n === i;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !on;
        videos(panel).forEach(function (v) {
          if (on) {
            try { v.currentTime = 0; } catch (e) {}
            tryPlay(v);
          } else {
            v.pause();
          }
        });
      });
      if (focus) tabs[i].focus();
    }
    api.select = select;

    function user() { if (api.onUser) api.onUser(); }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { user(); select(i, false); });
      tab.addEventListener('keydown', function (ev) {
        var next = -1;
        if (ev.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (ev.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (ev.key === 'Home') next = 0;
        else if (ev.key === 'End') next = tabs.length - 1;
        if (next < 0) return;
        ev.preventDefault();
        user();
        select(next, true);
      });
    });
    return api;
  }

  /* ---------- Image slider ---------- */
  /* Advances every 6 seconds and loops. Pauses while hovered, while a control or slide has
     keyboard focus, and for 10 seconds after any tap, swipe, arrow or tab (dot) use. Never
     advances while off screen or under reduced motion. */
  function initSlider(box, api) {
    if (!api) return;
    var n = api.tabs.length;
    var STEP = 6000, HOLD = 10000;
    var hovered = false, focused = false, visible = false, holdUntil = 0, timer = null;

    function go(step) { api.select((api.index + step + n) % n, false); }
    function running() { return !hovered && !focused && visible && !reduce && n > 1; }
    function clear() { if (timer) { clearTimeout(timer); timer = null; } }
    function schedule() {
      clear();
      if (!running()) return;
      var wait = Math.max(STEP, holdUntil - Date.now());
      timer = setTimeout(function () {
        timer = null;
        if (running() && Date.now() >= holdUntil) go(1);
        schedule();
      }, wait);
    }
    function sync() { if (running()) { if (!timer) schedule(); } else clear(); }
    function hold() { holdUntil = Date.now() + HOLD; schedule(); }
    api.onUser = hold;

    var prev = box.querySelector('[data-gg-prev]');
    var next = box.querySelector('[data-gg-next]');
    if (prev) prev.addEventListener('click', function () { hold(); go(-1); });
    if (next) next.addEventListener('click', function () { hold(); go(1); });

    var sx = 0, sy = 0, tracking = false;
    box.addEventListener('touchstart', function (ev) {
      hold();
      if (ev.touches.length !== 1) return;
      tracking = true; sx = ev.touches[0].clientX; sy = ev.touches[0].clientY;
    }, { passive: true });
    box.addEventListener('touchend', function (ev) {
      if (!tracking) return;
      tracking = false;
      var t = ev.changedTouches[0];
      var dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) { hold(); go(dx < 0 ? 1 : -1); }
    }, { passive: true });

    box.addEventListener('mouseenter', function () { hovered = true; sync(); });
    box.addEventListener('mouseleave', function () { hovered = false; sync(); });
    box.addEventListener('focusin', function (ev) {
      var kb = false; try { kb = ev.target.matches(':focus-visible'); } catch (x) {}
      if (kb) { focused = true; sync(); }
    });
    box.addEventListener('focusout', function (ev) {
      if (!box.contains(ev.relatedTarget)) { focused = false; sync(); }
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { visible = en.isIntersecting; sync(); });
      }, { threshold: 0.25 }).observe(box);
    } else {
      visible = true; sync();
    }
    box.ggSliderSync = sync;
    box.ggSliderState = function () { return { hovered: hovered, focused: focused, visible: visible, reduce: reduce, holdMs: Math.max(0, holdUntil - Date.now()), timer: !!timer, index: api.index }; };
  }

  /* ---------- Story cards ---------- */
  /* The row moves one whole card per step (arrows, dots, edge zones) and snaps to card starts, so
     desktop always shows 3 whole cards. Edge zones and the hover preview run only with a mouse
     ((hover: hover) and (pointer: fine)); touch keeps swipe, arrows, dots and the plus panel. */
  var mqFine = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  function fine() { return !!(mqFine && mqFine.matches); }
  var peekOpen = null;
  var quietFocus = false;
  var panelActive = false;

  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' && peekOpen) peekOpen.escape();
  });

  function initStory(box) {
    var track = box.querySelector('[data-gg-story-track]');
    if (!track) return;
    var cards = Array.prototype.slice.call(track.querySelectorAll('.gsc__card'));
    var controls = box.querySelector('[data-gg-story-controls]');
    var prev = box.querySelector('[data-gg-story-prev]');
    var next = box.querySelector('[data-gg-story-next]');
    var dots = Array.prototype.slice.call(box.querySelectorAll('[data-gg-story-dot]'));
    var zones = Array.prototype.slice.call(box.querySelectorAll('[data-gg-story-zone]'));
    var dialog = box.querySelector('[data-gg-story-dialog]');
    var body = box.querySelector('[data-gg-story-body]');
    var panels = Array.prototype.slice.call(box.querySelectorAll('template[data-gg-story-panel]'));
    var openers = Array.prototype.slice.call(box.querySelectorAll('[data-gg-story-open]'));
    var pending = 0, pendingUntil = 0;
    if (!cards.length) return;

    function maxScroll() { return Math.max(0, track.scrollWidth - track.clientWidth); }
    function offset(i) { return cards[i].offsetLeft - cards[0].offsetLeft; }
    function lastIndex() {
      var max = maxScroll();
      for (var i = 0; i < cards.length; i++) { if (offset(i) >= max - 1) return i; }
      return cards.length - 1;
    }
    function nearest() {
      var x = track.scrollLeft, best = 0, d = Infinity;
      cards.forEach(function (c, i) { var dd = Math.abs(offset(i) - x); if (dd < d) { d = dd; best = i; } });
      return best;
    }
    function goTo(i) {
      i = Math.max(0, Math.min(lastIndex(), i));
      pending = i; pendingUntil = Date.now() + 600;
      track.scrollTo({ left: Math.min(offset(i), maxScroll()), behavior: reduce ? 'auto' : 'smooth' });
    }
    function slide(dir) { goTo((Date.now() < pendingUntil ? pending : nearest()) + dir); }

    function update() {
      var max = maxScroll(), x = track.scrollLeft;
      var atStart = x <= 2, atEnd = x >= max - 2;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
      if (controls) controls.hidden = max <= 2;
      zones.forEach(function (z) {
        var hide = z.getAttribute('data-gg-story-zone') === 'prev' ? atStart : atEnd;
        z.hidden = hide;
        if (hide && z.ggStop) z.ggStop();
      });
      var tr = track.getBoundingClientRect();
      cards.forEach(function (c, i) {
        if (!dots[i]) return;
        var r = c.getBoundingClientRect();
        var on = Math.min(r.right, tr.right) - Math.max(r.left, tr.left) >= r.width * 0.9;
        dots[i].classList.toggle('is-active', on);
        dots[i].setAttribute('aria-current', on ? 'true' : 'false');
      });
    }
    if (prev) prev.addEventListener('click', function () { slide(-1); });
    if (next) next.addEventListener('click', function () { slide(1); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { goTo(parseInt(d.getAttribute('data-gg-story-dot'), 10) || 0); });
    });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    /* Edge zones: rest 300ms to move one card, then one more card every 750ms while the pointer stays. */
    zones.forEach(function (z) {
      var dir = z.getAttribute('data-gg-story-zone') === 'prev' ? -1 : 1;
      var t = null;
      function stop() { if (t) { clearTimeout(t); t = null; } }
      function tick() {
        t = null;
        if (!fine() || peekOpen || z.hidden) return;
        slide(dir);
        t = setTimeout(tick, 750);
      }
      z.addEventListener('mouseenter', function () { stop(); if (fine()) t = setTimeout(tick, 300); });
      z.addEventListener('mouseleave', stop);
      z.ggStop = stop;
    });
    function stopZones() { zones.forEach(function (z) { if (z.ggStop) z.ggStop(); }); }

    /* Hover preview: opens on plus hover or keyboard focus, closes 150ms after the pointer or focus
       leaves the card, or at once on Escape. One preview open on the page at a time. */
    function setAria() {
      openers.forEach(function (b) {
        if (!b.ggControls) b.ggControls = b.getAttribute('aria-controls') || '';
        var ids = b.ggControls.split(' ');
        if (fine()) {
          b.setAttribute('aria-controls', b.ggControls);
          if (!b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', 'false');
        } else {
          b.setAttribute('aria-controls', ids[ids.length - 1]);
          b.removeAttribute('aria-expanded');
        }
      });
    }
    setAria();
    listen(mqFine, function () { if (peekOpen) peekOpen.close(true); setAria(); });

    cards.forEach(function (card) {
      var plus = card.querySelector('[data-gg-story-open]');
      var peek = card.querySelector('[data-gg-story-peek]');
      if (!plus || !peek) return;
      var api = {}, closeT = null;
      var intro = peek.querySelector('.gsc__peek-intro');

      function setOpen(on) {
        card.classList.toggle('is-peek', on);
        box.classList.toggle('gsc--peeking', on);
        peek.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (on) peek.removeAttribute('inert'); else peek.setAttribute('inert', '');
        if (fine()) plus.setAttribute('aria-expanded', on ? 'true' : 'false');
      }
      function cancel() { if (closeT) { clearTimeout(closeT); closeT = null; } }
      function shut() {
        cancel();
        if (peekOpen === api) peekOpen = null;
        setOpen(false);
      }
      function open() {
        if (!fine()) return;
        cancel();
        if (peekOpen === api) return;
        if (peekOpen) peekOpen.close(true);
        peekOpen = api;
        stopZones();
        setOpen(true);
        if (intro) intro.classList.toggle('is-clamped', intro.scrollHeight > intro.clientHeight + 1);
      }
      api.close = function (now) {
        cancel();
        if (now) shut(); else closeT = setTimeout(shut, 150);
      };
      api.escape = function () {
        var inside = card.contains(document.activeElement) && document.activeElement !== plus;
        shut();
        if (inside) { quietFocus = true; plus.focus(); quietFocus = false; }
      };

      plus.addEventListener('mouseenter', open);
      plus.addEventListener('focus', function () {
        if (quietFocus || panelActive) return;
        var kb = false; try { kb = plus.matches(':focus-visible'); } catch (x) {}
        if (kb) open();
      });
      card.addEventListener('mouseenter', function () { if (peekOpen === api) cancel(); });
      card.addEventListener('mouseleave', function () { if (peekOpen === api) api.close(false); });
      card.addEventListener('focusout', function (ev) {
        if (peekOpen === api && !card.contains(ev.relatedTarget)) api.close(false);
      });
      card.addEventListener('focusin', function () { if (peekOpen === api) cancel(); });
    });

    /* Full screen panel */
    if (!dialog || !body || !panels.length || typeof dialog.showModal !== 'function') return;
    var dPrev = dialog.querySelector('[data-gg-story-dprev]');
    var dNext = dialog.querySelector('[data-gg-story-dnext]');
    var dClose = dialog.querySelector('[data-gg-story-close]');
    var current = 0;

    function show(i) {
      current = i;
      var tpl = panels[i];
      while (body.firstChild) body.removeChild(body.firstChild);
      body.appendChild(tpl.content.cloneNode(true));
      dialog.setAttribute('aria-labelledby', tpl.getAttribute('data-title-id'));
      dialog.scrollTop = 0;
      if (dPrev) dPrev.disabled = i === 0;
      if (dNext) dNext.disabled = i === panels.length - 1;
    }
    function openPanel(i) {
      if (peekOpen) peekOpen.close(true);
      show(i);
      panelActive = true;
      document.documentElement.classList.add('gsc-lock');
      dialog.showModal();
      if (dClose) dClose.focus();
    }
    function move(dir) {
      var i = current + dir;
      if (i < 0 || i >= panels.length) return;
      show(i);
      var btn = dir < 0 ? dPrev : dNext;
      if (btn && btn.disabled && dClose) dClose.focus();
    }

    openers.forEach(function (b) {
      b.addEventListener('click', function () { openPanel(parseInt(b.getAttribute('data-gg-story-open'), 10) || 0); });
    });
    Array.prototype.forEach.call(box.querySelectorAll('[data-gg-story-read]'), function (b) {
      b.addEventListener('click', function () { openPanel(parseInt(b.getAttribute('data-gg-story-read'), 10) || 0); });
    });
    if (dPrev) dPrev.addEventListener('click', function () { move(-1); });
    if (dNext) dNext.addEventListener('click', function () { move(1); });
    function unlock() { document.documentElement.classList.remove('gsc-lock'); }
    if (dClose) dClose.addEventListener('click', function () { dialog.close(); unlock(); });
    dialog.addEventListener('cancel', unlock);
    dialog.addEventListener('click', function (ev) {
      if (ev.target !== dialog) return;
      var r = dialog.getBoundingClientRect();
      if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) { dialog.close(); unlock(); }
    });
    dialog.addEventListener('close', function () {
      panelActive = false;
      unlock();
      while (body.firstChild) body.removeChild(body.firstChild);
      var back = openers[current];
      if (back) { quietFocus = true; back.focus(); quietFocus = false; }
    });
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-gg-tabs]'), function (box) {
      var api = initTabs(box);
      if (box.hasAttribute('data-gg-slider')) initSlider(box, api);
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-gg-story]'), initStory);
    initVideos();
    listen(mqReduce, function () {
      reduce = !!mqReduce.matches;
      if (reduce) stopAll();
      Array.prototype.forEach.call(document.querySelectorAll('[data-gg-slider]'), function (b) { if (b.ggSliderSync) b.ggSliderSync(); });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
